import React, { useContext, useState } from "react";
import Helmet from "react-helmet";
import { Link } from "react-router-dom";
import JobsContext from "../context/JobsContext";
import { getAvailableJobs } from "../data/jobs";
import Icon from "../components/Icon";
import installation from "../img/smith-installatie.jpg";
import solar from "../img/smith-zonnepanelen.jpg";
import solarLogo from "../img/smith-solar-logo.svg";
import tuinbouwLogo from "../img/smith-tuinbouwtechniek-logo.svg";

const benefits = [
    {
        icon: "sun",
        title: "Werk met betekenis",
        text: "Van zonnepanelen tot slimme techniek in de tuinbouw. Jouw werk draagt bij aan een duurzame toekomst.",
    },
    {
        icon: "growth",
        title: "Ruimte om te groeien",
        text: "Leer van ervaren collega’s, ontwikkel je vakkennis en ontdek waar jouw talent je kan brengen.",
    },
    {
        icon: "team",
        title: "Op z’n Westlands",
        text: "Korte lijnen, een nuchtere mentaliteit en collega’s die voor elkaar klaarstaan. Dat werkt wel zo prettig.",
    },
];

function Home() {
    const jobs = getAvailableJobs(useContext(JobsContext));
    const [category, setCategory] = useState("Alle vacatures");
    const categories = [
        "Alle vacatures",
        ...new Set(jobs.map((job) => job.category)),
    ];
    const visibleJobs = jobs.filter(
        (job) => category === "Alle vacatures" || job.category === category,
    );

    return (
        <>
            <Helmet>
                <title>Werken bij Smith - Jouw talent. Onze energie.</title>
                <meta
                    name="description"
                    content="Bouw mee aan een duurzame toekomst bij Smith Solar en Smith Tuinbouwtechniek in Maasdijk. Ontdek onze vacatures en vind de baan die bij jou past."
                />
            </Helmet>
            <section className="careers-hero" aria-labelledby="hero-title">
                <img
                    className="hero-photo"
                    src={installation}
                    alt="Een monteur aan het werk bij een zonnepaneleninstallatie"
                    width="1920"
                    height="1080"
                    fetchpriority="high"
                />
                <div className="site-container hero-inner">
                    <div className="hero-copy">
                        <span className="eyebrow hero-eyebrow">
                            <span className="status-dot" /> Wij zoeken collega’s
                        </span>
                        <h1 id="hero-title">
                            Jouw talent.
                            <br />
                            Onze energie<span className="orange-dot">.</span>
                        </h1>
                        <p>
                            Steek je handen uit de mouwen. Laat je talent
                            groeien.
                            <br className="desktop-break" /> En bouw met ons aan
                            een duurzame toekomst.
                        </p>
                        <div className="hero-actions">
                            <a
                                href="#vacatures"
                                className="button button-orange"
                            >
                                Ontdek jouw nieuwe baan <Icon />
                            </a>
                            <a href="#over-smith" className="hero-secondary">
                                Leer ons kennen <Icon name="down" />
                            </a>
                        </div>
                    </div>
                    <div className="hero-bottom">
                        <span>
                            <Icon name="pin" /> Gewoon in Maasdijk. Volop in
                            beweging.
                        </span>
                        <a href="#vacatures" className="hero-vacancies">
                            <span className="status-dot" />
                            {jobs.length === 0
                                ? "Open sollicitaties welkom"
                                : `${jobs.length} open ${jobs.length === 1 ? "vacature" : "vacatures"}`}
                            <Icon name="diagonal" />
                        </a>
                    </div>
                </div>
            </section>

            <div className="brand-strip">
                <div className="site-container brand-strip-inner">
                    <span>TWEE SPECIALISMEN. ÉÉN TEAM.</span>
                    <a href="https://smithsolar.nl/" className="company-logo">
                        <img
                            src={solarLogo}
                            alt="Smith Solar"
                            width="240"
                            height="70"
                        />
                        <Icon name="diagonal" />
                    </a>
                    <span className="brand-divider" />
                    <a
                        href="https://smithtuinbouwtechniek.nl/"
                        className="company-logo"
                    >
                        <img
                            src={tuinbouwLogo}
                            alt="Smith Tuinbouwtechniek"
                            width="240"
                            height="70"
                        />
                        <Icon name="diagonal" />
                    </a>
                </div>
            </div>

            <section
                className="section benefits-section"
                id="over-smith"
                aria-labelledby="benefits-title"
            >
                <div className="site-container">
                    <div className="section-heading">
                        <div>
                            <span className="eyebrow">
                                DIT IS WERKEN BIJ SMITH
                            </span>
                            <h2 id="benefits-title">
                                Goed werk begint
                                <br />
                                met een goed gevoel.
                            </h2>
                        </div>
                        <p>
                            Je wilt iets maken waar je trots op bent. Met mensen
                            op wie je kunt rekenen. Bij Smith krijg je de ruimte
                            om te doen waar je goed in bent.
                        </p>
                    </div>
                    <div className="benefits-grid">
                        {benefits.map((benefit, index) => (
                            <article className="benefit" key={benefit.title}>
                                <div className="benefit-top">
                                    <span className="benefit-icon">
                                        <Icon name={benefit.icon} />
                                    </span>
                                    <span className="benefit-number">
                                        0{index + 1}
                                    </span>
                                </div>
                                <h3>{benefit.title}</h3>
                                <p>{benefit.text}</p>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            <section
                className="section vacancies-section"
                id="vacatures"
                aria-labelledby="vacancies-title"
            >
                <div className="site-container">
                    <div className="section-heading">
                        <div>
                            <span className="eyebrow">JOUW VOLGENDE STAP</span>
                            <h2 id="vacancies-title">
                                Waar krijg jij energie van?
                            </h2>
                        </div>
                        <p>
                            Op kantoor of op het dak: jouw talent maakt het
                            verschil. Ontdek de baan die bij je past.
                        </p>
                    </div>
                    {jobs.length > 0 ? (
                        <>
                            <div className="vacancy-toolbar">
                                <div
                                    className="vacancy-filters"
                                    role="group"
                                    aria-label="Filter vacatures op vakgebied"
                                >
                                    {categories.map((item) => (
                                        <button
                                            key={item}
                                            type="button"
                                            aria-pressed={category === item}
                                            className={
                                                category === item
                                                    ? "filter-button active"
                                                    : "filter-button"
                                            }
                                            onClick={() => setCategory(item)}
                                        >
                                            {item}
                                            {item === "Alle vacatures" && (
                                                <span>{jobs.length}</span>
                                            )}
                                        </button>
                                    ))}
                                </div>
                                <span
                                    className="vacancy-result-count"
                                    role="status"
                                >
                                    {visibleJobs.length}{" "}
                                    {visibleJobs.length === 1
                                        ? "vacature"
                                        : "vacatures"}
                                </span>
                            </div>
                            <div className="vacancy-grid">
                                {visibleJobs.map((job) => (
                                    <Link
                                        to={job.path}
                                        className="vacancy-card"
                                        key={job.id}
                                    >
                                        <div className="vacancy-card-top">
                                            <span
                                                className={`category-label category-${job.category.toLowerCase()}`}
                                            >
                                                {job.category}
                                            </span>
                                            <Icon name="diagonal" />
                                        </div>
                                        <h3>{job.title}</h3>
                                        <div className="job-meta">
                                            <span>
                                                <Icon name="pin" />
                                                {job.location}
                                            </span>
                                            <span>
                                                <Icon name="clock" />
                                                {job.hours}
                                            </span>
                                            <span>
                                                <Icon name="education" />
                                                {job.education}
                                            </span>
                                        </div>
                                        <p>
                                            {job.id === "elektromonteur"
                                                ? "Van tekening naar een werkende installatie. Ga aan de slag met zonnepanelen en elektrotechniek, samen met collega’s die hun vak verstaan."
                                                : job.id ===
                                                    "commercieelMedewerkerBinnendienst"
                                                  ? "Jij ziet kansen en legt makkelijk contact. Help klanten op weg naar duurzame energie en geef onze commerciële ambities een boost."
                                                  : job.description}
                                        </p>
                                        <div className="vacancy-card-bottom">
                                            <span>Bekijk de vacature</span>
                                            <span className="card-arrow">
                                                <Icon />
                                            </span>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        </>
                    ) : (
                        <div className="vacancies-empty">
                            <Icon name="briefcase" />
                            <h3>Jouw talent is altijd welkom.</h3>
                            <p>
                                Op dit moment hebben we geen openstaande
                                vacatures. Denk je dat je bij Smith past? Stuur
                                ons een open sollicitatie.
                            </p>
                            <Link
                                to="/open-sollicitatie"
                                className="button button-dark"
                            >
                                Stuur een open sollicitatie <Icon />
                            </Link>
                        </div>
                    )}
                    {jobs.length > 0 && (
                        <div className="open-application">
                            <span>
                                Jouw baan er nog niet tussen? We maken graag
                                kennis met je.
                            </span>
                            <Link to="/open-sollicitatie">
                                Stuur een open sollicitatie <Icon />
                            </Link>
                        </div>
                    )}
                </div>
            </section>

            <section
                className="section story-section"
                aria-labelledby="story-title"
            >
                <div className="site-container story-grid">
                    <div className="story-image">
                        <img
                            src={solar}
                            alt="Zonnepanelen vangen de zon op voor duurzame energie"
                            width="1280"
                            height="853"
                            loading="lazy"
                        />
                        <div className="image-note">
                            <Icon name="sun" />
                            <span>
                                Mooi werk.
                                <br />
                                <strong>Een duurzame toekomst.</strong>
                            </span>
                        </div>
                    </div>
                    <div className="story-copy">
                        <span className="eyebrow">
                            SAMEN KRIJGEN WE HET VOOR ELKAAR
                        </span>
                        <h2 id="story-title">
                            Nuchtere aanpakkers.
                            <br />
                            Grootse plannen.
                        </h2>
                        <p>
                            Bij Smith Solar en Smith Tuinbouwtechniek komen
                            duurzame energie en slimme techniek samen. Vanuit
                            het Westland werken we aan projecten waar we trots
                            op zijn.
                        </p>
                        <p>
                            Of je nu klanten adviseert, installaties maakt of
                            alles achter de schermen regelt: je doet het samen.
                            Met korte lijnen, ruimte voor jouw ideeën en plezier
                            in je werk.
                        </p>
                        <ul className="story-checks">
                            <li>
                                <Icon name="check" />
                                Een betrokken team met kennis van het vak
                            </li>
                            <li>
                                <Icon name="check" />
                                Afwisselend werk dat ertoe doet
                            </li>
                            <li>
                                <Icon name="check" />
                                Ruimte voor jouw ontwikkeling
                            </li>
                        </ul>
                        <a href="#vacatures" className="text-link">
                            Word jij onze nieuwe collega? <Icon />
                        </a>
                    </div>
                </div>
            </section>

            <section
                className="contact-section"
                id="kennismaken"
                aria-labelledby="contact-title"
            >
                <div className="site-container contact-inner">
                    <div>
                        <span className="eyebrow">
                            DE EERSTE STAP IS ZO GEZET
                        </span>
                        <h2 id="contact-title">Zullen we kennismaken?</h2>
                        <p>
                            Een nieuwe baan begint met een goed gesprek. Heb je
                            een vraag
                            <br className="desktop-break" /> of wil je weten of
                            Smith bij je past? We horen graag van je.
                        </p>
                    </div>
                    <div className="contact-actions">
                        <a
                            className="button button-dark"
                            href="mailto:hr@smithtechniek.nl"
                        >
                            Stuur ons een bericht <Icon name="mail" />
                        </a>
                        <a
                            className="contact-email"
                            href="mailto:hr@smithtechniek.nl"
                        >
                            hr@smithtechniek.nl <Icon name="diagonal" />
                        </a>
                    </div>
                </div>
            </section>
        </>
    );
}
export default Home;
